import { Tooltip } from 'antd';
import { groupBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectSource } from 'api/project';
import { ReactComponent as TaskTemplateIcon } from 'assets/svgs/save-task-template.svg';
import { ReactComponent as SearchSvg } from 'assets/svgs/search.svg';
import { TaskTemplateLight } from 'data/templates/tasks';
import { CheckBoxValue } from '../drawer-footer';
import {
  RoundedCheckbox,
  ScrollableView,
  TemplateNameHeader,
  TemplateItem,
  RoadMap,
  ShowSavedTemplates,
  EmptyTemplateList,
  TemplateEllipsis,
  TemplateSection,
  TaskTemplateName,
} from '../styles';
import { DrawerContentTopBar } from './drawer-content-top';

interface DrawerFirstPhaseProps {
  sourceTemplates?: ProjectSource[];
  changeCurrentProjectTo: (template: TaskTemplateLight | null) => void;
  taskTemplates?: TaskTemplateLight[];
  currentShowingTemplate: TaskTemplateLight | null;
  setSearchableTemplates: (value: TaskTemplateLight[] | undefined) => void;
  searchableTemplates: TaskTemplateLight[] | undefined;
  onChangeCheckbox: (template: TaskTemplateLight) => void;
  checkBoxValues: CheckBoxValue[];
  setCheckBoxValues: (value: React.SetStateAction<CheckBoxValue[]>) => void;
}

export const DrawerFirstPhase: FC<DrawerFirstPhaseProps> = ({
  sourceTemplates,
  changeCurrentProjectTo,
  taskTemplates,
  currentShowingTemplate,
  setSearchableTemplates,
  searchableTemplates,
  onChangeCheckbox,
  checkBoxValues,
  setCheckBoxValues,
}) => {
  const templatesMap = useMemo(
    () => groupBy(searchableTemplates, 'parentId'),
    [searchableTemplates],
  );

  const checkBoxMap = useMemo(
    () => groupBy(checkBoxValues, 'template.parentId'),
    [checkBoxValues],
  );

  const templates =
    currentShowingTemplate === null
      ? Object.values(templatesMap)
      : [templatesMap[currentShowingTemplate?.parentId || 0] || []];

  const selectAll = (parentId: number, isChecked: boolean) => {
    setCheckBoxValues((values) => {
      return values.map((value) => ({
        ...value,
        checked:
          value.template.parentId === parentId ? isChecked : value.checked,
      }));
    });
  };

  return (
    <>
      <DrawerContentTopBar
        sourceTemplates={sourceTemplates}
        changeCurrentProjectTo={changeCurrentProjectTo}
        taskTemplates={taskTemplates}
        currentShowingTemplate={currentShowingTemplate}
        setSearchableTemplates={setSearchableTemplates}
      />

      <ScrollableView>
        {templates.length > 0 && templates[0].length > 0 ? (
          templates.map((taskTemplateGroups) => {
            const isAllChecked = checkBoxMap[
              taskTemplateGroups[0].parentId
            ].every((checkBox) => checkBox.checked);

            return (
              <TemplateSection key={taskTemplateGroups[0].parentId}>
                <TemplateNameHeader>
                  <ShowSavedTemplates>
                    <RoadMap />
                    <TemplateEllipsis>
                      {taskTemplateGroups[0].parentName}
                    </TemplateEllipsis>
                  </ShowSavedTemplates>
                  <Tooltip
                    placement="left"
                    title={<FormattedMessage id="select_all" />}
                  >
                    <RoundedCheckbox
                      checked={isAllChecked}
                      onChange={(e) => {
                        selectAll(
                          taskTemplateGroups[0].parentId,
                          e.target.checked,
                        );
                      }}
                    />
                  </Tooltip>
                </TemplateNameHeader>
                {taskTemplateGroups.map((taskTemplate) => {
                  const isChecked = checkBoxValues.find(
                    (checkBoxValue) =>
                      checkBoxValue.template.templateId ===
                      taskTemplate.templateId,
                  )?.checked;

                  return (
                    <TemplateItem
                      key={taskTemplate.templateId}
                      selected={isChecked}
                      onClick={() => onChangeCheckbox(taskTemplate)}
                    >
                      <ShowSavedTemplates>
                        <TaskTemplateIcon />
                        <TaskTemplateName>
                          {taskTemplate.templateName}
                        </TaskTemplateName>
                      </ShowSavedTemplates>
                      <RoundedCheckbox checked={isChecked} />
                    </TemplateItem>
                  );
                })}
              </TemplateSection>
            );
          })
        ) : (
          <EmptyTemplateList>
            <SearchSvg />
            <div>
              <FormattedMessage id="projects.task_template.drawer.search.error.no_search_result_line_1" />
            </div>
            <div>
              <FormattedMessage id="projects.task_template.drawer.search.error.no_search_result_line_2" />
            </div>
          </EmptyTemplateList>
        )}
      </ScrollableView>
    </>
  );
};
